import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMemberCoupons } from '@/hooks/useMemberCoupons';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const { totalItems } = useCart();
  const { member } = useAuth();
  const { data: memberCoupons } = useMemberCoupons(true);
  const availableCouponCount = member ? (memberCoupons?.length || 0) : 0;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isSkinActive = location.pathname.startsWith('/skin-analysis');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
      {/* 頂部公告列 */}
      <div className="bg-brand-sand border-b border-border/40">
        <div className="container mx-auto px-4 lg:px-8">
          <p className="text-center text-foreground/80 font-sans-tc text-[11px] md:text-xs tracking-wide py-2 leading-tight">
            <span className="hidden md:inline">✨ 全館滿 NT$1,500 免運費 ｜ 新會員首購享 9 折優惠</span>
            <span className="md:hidden">✨ 滿 NT$1,500 免運 ｜ 首購享 9 折</span>
          </p>
        </div>
      </div>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="font-serif text-xl lg:text-2xl font-light tracking-[0.15em] text-foreground">
              VELIREO SKIN
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              首頁
            </Link>
            <Link 
              to="/product-details" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              產品
            </Link>
            <Link 
              to="/promotion" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              優惠折扣
            </Link>
            <Link 
              to="/brand-story" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              品牌故事
            </Link>
            <Link 
              to="/articles" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              保養文章
            </Link>
            <Link
              to="/skin-analysis"
              className={`text-sm tracking-widest uppercase transition-colors duration-300 ${
                isSkinActive
                  ? 'text-[#C9817A] border-b-2 border-[#C9817A] pb-0.5'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              肌膚分析
            </Link>
            <Link 
              to="/ai-support" 
              className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              智能客服
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="ghost" size="icon" className="relative hidden md:inline-flex" aria-label="會員專區">
                  <Link to={availableCouponCount > 0 ? '/my-coupons' : '/login'}>
                    <User className="h-5 w-5" />
                    {availableCouponCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full text-[11px] leading-none font-medium flex items-center justify-center text-white"
                        style={{ backgroundColor: '#C9817A' }}
                        aria-label={`${availableCouponCount} 張可用優惠券`}
                      >
                        {availableCouponCount}
                      </span>
                    )}
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>會員專區</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="ghost" size="icon" className="relative" aria-label="購物車">
                  <Link to="/cart">
                    <ShoppingBag className="h-5 w-5" />
                    {totalItems > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                        {totalItems}
                      </span>
                    )}
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>購物車</p>
              </TooltipContent>
            </Tooltip>
            
            {/* Mobile Menu Toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              aria-label={isMobileMenuOpen ? "關閉選單" : "開啟選單"}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background border-b border-border"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
              <Link 
                to="/" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                首頁
              </Link>
              <Link 
                to="/product-details" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                產品
              </Link>
              <Link 
                to="/promotion" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                優惠折扣
              </Link>
              <Link 
                to="/brand-story" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                品牌故事
              </Link>
              <Link 
                to="/articles" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                保養文章
              </Link>
              <Link
                to="/skin-analysis"
                className={`text-sm tracking-widest uppercase transition-colors py-2 ${
                  isSkinActive ? 'text-[#C9817A]' : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                肌膚分析
              </Link>
              <Link 
                to="/ai-support" 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                智能客服
              </Link>
              <Link 
                to={availableCouponCount > 0 ? '/my-coupons' : '/login'} 
                className="text-sm tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors py-2 flex items-center gap-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                會員專區
                {availableCouponCount > 0 && (
                  <span
                    className="h-4 min-w-4 px-1 rounded-full text-[11px] leading-none font-medium flex items-center justify-center text-white"
                    style={{ backgroundColor: '#C9817A' }}
                    aria-label={`${availableCouponCount} 張可用優惠券`}
                  >
                    {availableCouponCount}
                  </span>
                )}
              </Link>

            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
